import { LoadBalancer, Server, Vpc } from '../ApiModel';

export enum ResourceType {
    Server,
    VPC,
    DomainRegistration,
    DomainNameHosting,
    LoadBalancer,
}

export function serverRoute(server: Server) {
    return `/server/${server.name}`;
}

export function domainRoute(domainName: string) {
    return `/domain/${domainName}`;
}

export function networkRoute(vpc: Vpc | number) {
    const id = typeof vpc === 'number' ? vpc : vpc.id;
    return `/network/${id}`;
}

export function loadBalancerRoute(lb: LoadBalancer) {
    return `/loadbalancer/${lb.id}`;
}

export const HomeRoute = '/home';
export const ServicesRoute = '/services';
export const BillingPendingRoute = '/billing/pending';
export const BillingInvoicesRoute = '/billing/invoices';
export const BillingPaymentDetailsRoute = '/billing/payment-details';
export const SSHKeysRoute = '/ssh-keys';
export const APIInfoRoute = '/api-info';
export const SupportRoute = '/support';
/** Basename-relative paths, for the route table inside the support SPA section. */
export const FreshdeskSsoPath = '/freshdesk-sso';
export const ZendeskSsoPath = '/zendesk-sso';
export const LoginRoute = '/login';
export const RegistrationRoute = '/register';
export const SignupRoute = '/signup';
export const LogoutRoute = '/logout';

/**
 * Whether `path` is the section root `section` or a path beneath it. Matching on the section root alone
 * is not enough — a bare `startsWith` would also match a sibling whose name merely begins with the same
 * characters (`/supportive`, `/support-portal`), silently extending whatever the caller gates on it.
 */
export function isWithinSection(path: string, section: string): boolean {
    return path === section || path.startsWith(`${section}/`);
}

/**
 * Whether `url` stays inside this page's SPA, so the router's history can carry it rather than a full
 * page load. A relative url is resolved by the router against the current location, so it never leaves;
 * an absolute one always does, since it cannot match a rooted base path. The section test runs on the
 * path alone because a query or fragment sitting directly on the base path (`/support?tab=x`) is still
 * inside the SPA, yet would fail a `${basePath}/` prefix test.
 *
 * The comparison ignores case, matching the router's own basename matching: a base path carrying a
 * URL-derived segment (`/server/{hostname}`) keeps whatever case the request used, while a full path
 * built from the stored name is lower-case, so a case-sensitive test would reload the page the
 * router already treats as its own.
 */
export function isInternalNavigation(url: string, basePath: string): boolean {
    if (!url.startsWith('/') && !url.startsWith('http://') && !url.startsWith('https://')) return true;
    // split always yields at least one element; the fallback is only to satisfy noUncheckedIndexedAccess.
    const path = url.split(/[?#]/)[0] ?? url;
    return isWithinSection(path.toLowerCase(), basePath.toLowerCase());
}

/** Whether `url` contains any of `segments` as a substring — a looser match than isWithinSection's path-boundary test. */
export function hasSegments(url: string, segments: string[]): boolean {
    return segments.some((seg) => url.includes(seg));
}

export function resourceListRoute(resourceType: ResourceType): string {
    switch (resourceType) {
        case ResourceType.Server:
            return `/services/servers`;

        case ResourceType.VPC:
            return `/services/networks`;

        case ResourceType.LoadBalancer:
            return `/services/loadbalancers`;

        case ResourceType.DomainRegistration:
        case ResourceType.DomainNameHosting:
            return `/domains`;
    }
}

export function resourceCreateRoute(resourceType: ResourceType): string {
    switch (resourceType) {
        case ResourceType.Server:
            return '/add-server';
        case ResourceType.VPC:
            return `/add-network`;
        case ResourceType.LoadBalancer:
            return `/add-loadbalancer`;
        case ResourceType.DomainNameHosting:
            return '/add-domain-hosting';
        case ResourceType.DomainRegistration:
            return '/add-domain-registration';
    }
}
